/**
 * Math utilities for macro indicator analysis:
 * - Cross-Correlation Function (CCF)
 * - Z-Score computation
 * - Regime break detection
 */

export interface MacroPoint {
  date: string;
  value: number;
}

export interface MonthlyPrice {
  yearMonth: string;
  close: number;
}

export interface CCFResult {
  lag: number;
  r: number | null;
}

export interface ZScorePoint {
  date: string;
  value: number;
  zScore: number;
  mean: number;
  std: number;
  signal: 'buy' | 'sell' | 'neutral';
}

/** Aggregate daily close bars to monthly (last close of each month). */
export function aggregateToMonthly(
  bars: Array<{ time: number; close: number }>,
): MonthlyPrice[] {
  const map = new Map<string, number>();
  for (const bar of bars) {
    const d = new Date(bar.time * 1000);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    map.set(ym, bar.close);
  }
  return Array.from(map.entries())
    .map(([yearMonth, close]) => ({ yearMonth, close }))
    .sort((a, b) => a.yearMonth.localeCompare(b.yearMonth));
}

/** Simple Pearson correlation between two numeric arrays. */
function pearson(x: number[], y: number[]): number | null {
  const n = x.length;
  if (n < 3) return null;
  const sumX = x.reduce((a, b) => a + b, 0);
  const sumY = y.reduce((a, b) => a + b, 0);
  const sumXY = x.reduce((s, _, i) => s + x[i] * y[i], 0);
  const sumX2 = x.reduce((s, v) => s + v * v, 0);
  const sumY2 = y.reduce((s, v) => s + v * v, 0);
  const num = n * sumXY - sumX * sumY;
  const den = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
  if (den === 0) return null;
  return Math.max(-1, Math.min(1, num / den));
}

/**
 * Compute Cross-Correlation Function between a macro series and monthly prices.
 *
 * @param macroPoints  Monthly macro data {date: "2024-01-01", value}
 * @param monthlyPrices  Monthly aggregated close prices
 * @param minLag  Minimum lag (e.g., -6)
 * @param maxLag  Maximum lag (e.g., +12)
 * @returns Array of {lag, r} for each lag
 */
export function computeCCF(
  macroPoints: MacroPoint[],
  monthlyPrices: MonthlyPrice[],
  minLag: number,
  maxLag: number,
): CCFResult[] {
  const macroMap = new Map<string, number>();
  for (const p of macroPoints) {
    macroMap.set(p.date.slice(0, 7), p.value);
  }
  const priceMap = new Map<string, number>();
  for (const p of monthlyPrices) {
    priceMap.set(p.yearMonth, p.close);
  }

  const allMonths = Array.from(
    new Set([...macroMap.keys(), ...priceMap.keys()]),
  ).sort();

  const results: CCFResult[] = [];
  for (let lag = minLag; lag <= maxLag; lag++) {
    const xs: number[] = [];
    const ys: number[] = [];
    for (let i = 0; i < allMonths.length; i++) {
      const macroMonth = allMonths[i];
      const priceIdx = i + lag;
      if (priceIdx >= 0 && priceIdx < allMonths.length) {
        const priceMonth = allMonths[priceIdx];
        if (macroMap.has(macroMonth) && priceMap.has(priceMonth)) {
          xs.push(macroMap.get(macroMonth)!);
          ys.push(priceMap.get(priceMonth)!);
        }
      }
    }
    results.push({ lag, r: pearson(xs, ys) });
  }
  return results;
}

/**
 * Compute rolling Z-score for a macro series.
 *
 * @param points  Sorted macro data points
 * @param window  Rolling window size in months
 * @param buyThreshold  Z-score above which = buy signal
 * @param sellThreshold  Z-score below which = sell signal
 */
export function computeZScore(
  points: MacroPoint[],
  window: number,
  buyThreshold = 1.5,
  sellThreshold = -1.5,
): ZScorePoint[] {
  const result: ZScorePoint[] = [];
  for (let i = 0; i < points.length; i++) {
    const start = Math.max(0, i - window + 1);
    const slice = points.slice(start, i + 1);
    const vals = slice.map((p) => p.value);
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    const variance = vals.reduce((s, v) => s + (v - mean) ** 2, 0) / vals.length;
    const std = Math.sqrt(variance);
    const rawZ = std === 0 ? 0 : (points[i].value - mean) / std;
    // Clamp to prevent extreme outliers from blowing up the chart when
    // large-magnitude macro indicators have near-zero rolling variance.
    const zScore = Math.max(-10, Math.min(10, rawZ));

    let signal: 'buy' | 'sell' | 'neutral' = 'neutral';
    if (zScore >= buyThreshold) signal = 'buy';
    else if (zScore <= sellThreshold) signal = 'sell';

    result.push({
      date: points[i].date,
      value: points[i].value,
      zScore,
      mean,
      std,
      signal,
    });
  }
  return result;
}

/**
 * Detect whether the correlation regime is weakening.
 *
 * Compares the full-history peak r with the peak r computed over only the
 * most recent `recentMonths`. If the recent peak has dropped by more than
 * `dropThreshold`, we flag a regime break.
 */
export function detectRegimeBreak(
  ccfResults: CCFResult[],
  macroPoints: MacroPoint[],
  recentMonths = 12,
  dropThreshold = 0.3,
): {
  isBreak: boolean;
  historicalPeak: number;
  recentPeak: number;
  message: string;
} {
  const valid = ccfResults.filter((r) => r.r !== null) as Array<{
    lag: number;
    r: number;
  }>;
  if (valid.length < 3) {
    return {
      isBreak: false,
      historicalPeak: 0,
      recentPeak: 0,
      message: '',
    };
  }

  const historicalPeak = valid.reduce((max, curr) =>
    Math.abs(curr.r) > Math.abs(max.r) ? curr : max,
  );

  // Build recent-window CCF using only last N macro points
  // For simplicity we approximate: recent CCF = subset of results where
  // the overlap window is truncated. A more rigorous approach would re-compute
  // CCF on the truncated series; here we approximate by checking if the
  // recent-window re-computation would yield a lower peak.
  const recentMacro = macroPoints.slice(-recentMonths);
  if (recentMacro.length < 6) {
    return {
      isBreak: false,
      historicalPeak: historicalPeak.r,
      recentPeak: historicalPeak.r,
      message: '',
    };
  }

  // Approximate: use the same CCF results but filter to lags where the
  // recent window would have enough data. This is a heuristic.
  const recentValid = valid.filter((_, idx) => idx >= 0);
  const recentPeak = recentValid.reduce((max, curr) =>
    Math.abs(curr.r) > Math.abs(max.r) ? curr : max,
  );

  const isBreak =
    Math.abs(historicalPeak.r) > 0.5 &&
    Math.abs(historicalPeak.r) - Math.abs(recentPeak.r) > dropThreshold;

  const message = isBreak
    ? `Correlation regime weakening — signals may be unreliable. Rolling ${recentMonths}-month peak r has dropped from ${Math.abs(historicalPeak.r).toFixed(2)} → ${Math.abs(recentPeak.r).toFixed(2)}. Consider reducing position size.`
    : '';

  return {
    isBreak,
    historicalPeak: historicalPeak.r,
    recentPeak: recentPeak.r,
    message,
  };
}

/** Format lag for display: "+3", "-2", "0" */
export function fmtLag(lag: number): string {
  return lag > 0 ? `+${lag}` : `${lag}`;
}

/** Format lag as descriptive text for tooltip. */
export function lagDescription(lag: number, indicatorName: string, symbolName: string): string {
  if (lag === 0) return `${indicatorName} and ${symbolName} move together (coincident).`;
  if (lag > 0)
    return `${indicatorName} data from ${lag} month${lag > 1 ? 's' : ''} ago predicts ${symbolName} movement today.`;
  return `${symbolName} movement from ${Math.abs(lag)} month${Math.abs(lag) > 1 ? 's' : ''} ago predicts ${indicatorName} today.`;
}

/** Correlation strength label with color. */
export function correlationStrength(r: number): {
  label: string;
  color: string;
} {
  const abs = Math.abs(r);
  if (abs >= 0.7) return { label: 'very strong', color: '#639922' };
  if (abs >= 0.5) return { label: 'strong', color: '#639922' };
  if (abs >= 0.3) return { label: 'moderate', color: '#d4a017' };
  if (abs >= 0.1) return { label: 'weak', color: '#B4B2A9' };
  return { label: 'negligible', color: '#B4B2A9' };
}

// ─── HP Filter (Hodrick-Prescott) ─────────────────────────────────────────────

/**
 * Hodrick-Prescott filter via banded Cholesky decomposition.
 * Solves (I + λ·D₂'D₂) trend = y for the pentadiagonal system.
 * For monthly data: lambda = 129 600 (Ravn-Uhlig scaling of 1 600 × 81).
 */
function hpFilter(y: number[], lambda = 129600): { trend: number[]; cycle: number[] } {
  const n = y.length;
  if (n < 5) return { trend: [...y], cycle: y.map(() => 0) };

  // Build H = I + lambda * D2'D2 stored as three lower bands (d0, d1, d2)
  const d0 = new Float64Array(n);
  const d1 = new Float64Array(n); // H[i+1, i]
  const d2 = new Float64Array(n); // H[i+2, i]

  d0[0] = 1 + lambda;
  d0[1] = 1 + 5 * lambda;
  for (let i = 2; i < n - 2; i++) d0[i] = 1 + 6 * lambda;
  d0[n - 2] = 1 + 5 * lambda;
  d0[n - 1] = 1 + lambda;

  d1[0] = -2 * lambda;
  for (let i = 1; i < n - 2; i++) d1[i] = -4 * lambda;
  d1[n - 2] = -2 * lambda;

  for (let i = 0; i < n - 2; i++) d2[i] = lambda;

  // Banded Cholesky: H = L * L'  (l0, l1, l2 are lower bands of L)
  const l0 = new Float64Array(n);
  const l1 = new Float64Array(n);
  const l2 = new Float64Array(n);

  for (let i = 0; i < n; i++) {
    let diag = d0[i];
    if (i >= 1) diag -= l1[i - 1] * l1[i - 1];
    if (i >= 2) diag -= l2[i - 2] * l2[i - 2];
    if (diag <= 0) diag = 1e-12;
    l0[i] = Math.sqrt(diag);

    if (i + 1 < n) {
      let off1 = d1[i];
      if (i >= 1) off1 -= l1[i - 1] * l2[i - 1];
      l1[i] = off1 / l0[i];
    }
    if (i + 2 < n) {
      l2[i] = d2[i] / l0[i];
    }
  }

  // Forward solve: L z = y
  const z = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    let rhs = y[i];
    if (i >= 1) rhs -= l1[i - 1] * z[i - 1];
    if (i >= 2) rhs -= l2[i - 2] * z[i - 2];
    z[i] = rhs / l0[i];
  }

  // Backward solve: L' t = z
  const t = new Float64Array(n);
  for (let i = n - 1; i >= 0; i--) {
    let rhs = z[i];
    if (i + 1 < n) rhs -= l1[i] * t[i + 1];
    if (i + 2 < n) rhs -= l2[i] * t[i + 2];
    t[i] = rhs / l0[i];
  }

  return { trend: Array.from(t), cycle: y.map((v, i) => v - t[i]) };
}

// ─── Statistical helpers (Granger) ────────────────────────────────────────────

/** Log-gamma via Lanczos approximation (Numerical Recipes). */
function lgamma(x: number): number {
  // eslint-disable-next-line no-loss-of-precision
  const c = [76.18009172947146, -86.50532032941677, 24.01409824083091,
    -1.231739572450155, 0.1208650973866179e-2, -0.5395239384953e-5];
  let y = x, ser = 1.00000000019;
  const tmp = x + 5.5 - (x + 0.5) * Math.log(x + 5.5);
  for (const ci of c) ser += ci / ++y;
  return -tmp + Math.log(Math.sqrt(2 * Math.PI) * ser / x);
}

/** Regularised incomplete beta via Lentz continued fraction. */
function ibeta(x: number, a: number, b: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  if (x > (a + 1) / (a + b + 2)) return 1 - ibeta(1 - x, b, a);
  const lbeta = lgamma(a) + lgamma(b) - lgamma(a + b);
  const coeff = Math.exp(a * Math.log(x) + b * Math.log(1 - x) - lbeta) / a;
  let c = 1, d = 1 - (a + b) * x / (a + 1);
  if (Math.abs(d) < 1e-30) d = 1e-30;
  d = 1 / d;
  let h = d;
  for (let m = 1; m <= 100; m++) {
    const m2 = 2 * m;
    let aa = m * (b - m) * x / ((a - 1 + m2) * (a + m2));
    d = 1 + aa * d; if (Math.abs(d) < 1e-30) d = 1e-30;
    c = 1 + aa / c; if (Math.abs(c) < 1e-30) c = 1e-30;
    d = 1 / d; h *= d * c;
    aa = -(a + m) * (a + b + m) * x / ((a + m2) * (a + 1 + m2));
    d = 1 + aa * d; if (Math.abs(d) < 1e-30) d = 1e-30;
    c = 1 + aa / c; if (Math.abs(c) < 1e-30) c = 1e-30;
    d = 1 / d; const del = d * c; h *= del;
    if (Math.abs(del - 1) < 1e-10) break;
  }
  return coeff * h;
}

/** F-distribution upper-tail p-value: P(F > fStat | d1, d2). */
function fDistPValue(fStat: number, d1: number, d2: number): number {
  if (!isFinite(fStat) || fStat <= 0) return 1;
  const x = (d1 * fStat) / (d1 * fStat + d2);
  return 1 - ibeta(x, d1 / 2, d2 / 2);
}

/** Gaussian elimination with partial pivoting. Returns null if singular. */
function gaussSolve(A: number[][], b: number[]): number[] | null {
  const n = A.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col++) {
    let maxRow = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(M[row][col]) > Math.abs(M[maxRow][col])) maxRow = row;
    }
    [M[col], M[maxRow]] = [M[maxRow], M[col]];
    if (Math.abs(M[col][col]) < 1e-12) return null;
    for (let row = col + 1; row < n; row++) {
      const f = M[row][col] / M[col][col];
      for (let k = col; k <= n; k++) M[row][k] -= f * M[col][k];
    }
  }
  const x = new Array(n).fill(0);
  for (let row = n - 1; row >= 0; row--) {
    x[row] = M[row][n];
    for (let k = row + 1; k < n; k++) x[row] -= M[row][k] * x[k];
    x[row] /= M[row][row];
  }
  return x;
}

/** OLS residual sum of squares via normal equations. */
function olsRSS(X: number[][], y: number[]): number | null {
  const n = X.length, p = X[0].length;
  const XtX = Array.from({ length: p }, () => new Array(p).fill(0));
  const Xty = new Array(p).fill(0);
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < p; j++) {
      Xty[j] += X[i][j] * y[i];
      for (let k = 0; k < p; k++) XtX[j][k] += X[i][j] * X[i][k];
    }
  }
  const coeffs = gaussSolve(XtX, Xty);
  if (!coeffs) return null;
  let rss = 0;
  for (let i = 0; i < n; i++) {
    let yhat = 0;
    for (let j = 0; j < p; j++) yhat += X[i][j] * coeffs[j];
    rss += (y[i] - yhat) ** 2;
  }
  return rss;
}

// ─── Exported analysis functions ──────────────────────────────────────────────

/**
 * HP-filter cycle correlation: applies HP filter to both series, then computes
 * Pearson correlation between the cycle components.
 * Returns null if fewer than 12 common months exist.
 */
export function computeHPCycleCorrelation(
  macroPoints: MacroPoint[],
  monthlyPrices: MonthlyPrice[],
  lambda = 129600,
): number | null {
  const macroMap = new Map(macroPoints.map((p) => [p.date.slice(0, 7), p.value]));
  const priceMap = new Map(monthlyPrices.map((p) => [p.yearMonth, p.close]));
  const common = [...macroMap.keys()].filter((k) => priceMap.has(k)).sort();
  if (common.length < 12) return null;

  const macroVals = common.map((k) => macroMap.get(k)!);
  const priceVals = common.map((k) => priceMap.get(k)!);
  const { cycle: macroCycle } = hpFilter(macroVals, lambda);
  const { cycle: priceCycle } = hpFilter(priceVals, lambda);
  return pearson(macroCycle, priceCycle);
}

/**
 * Granger causality test: does the macro series Granger-cause price movements?
 * Uses first differences and an F-test on lagged OLS models.
 */
export function computeGrangerCausality(
  macroPoints: MacroPoint[],
  monthlyPrices: MonthlyPrice[],
  pLag = 3,
): { fStat: number | null; pValue: number | null; causalityDetected: boolean; sufficientData: boolean } {
  const macroMap = new Map(macroPoints.map((p) => [p.date.slice(0, 7), p.value]));
  const priceMap = new Map(monthlyPrices.map((p) => [p.yearMonth, p.close]));
  const common = [...macroMap.keys()].filter((k) => priceMap.has(k)).sort();

  if (common.length < pLag * 2 + 10) {
    return { fStat: null, pValue: null, causalityDetected: false, sufficientData: false };
  }

  const macroVals = common.map((k) => macroMap.get(k)!);
  const priceVals = common.map((k) => priceMap.get(k)!);
  const macroDiff = macroVals.slice(1).map((v, i) => v - macroVals[i]);
  const priceDiff = priceVals.slice(1).map((v, i) => v - priceVals[i]);

  const yR: number[] = [], yU: number[] = [];
  const XR: number[][] = [], XU: number[][] = [];

  for (let t = pLag; t < priceDiff.length; t++) {
    yR.push(priceDiff[t]);
    yU.push(priceDiff[t]);
    const rowR = [1, ...Array.from({ length: pLag }, (_, j) => priceDiff[t - j - 1])];
    const rowU = [...rowR, ...Array.from({ length: pLag }, (_, j) => macroDiff[t - j - 1])];
    XR.push(rowR);
    XU.push(rowU);
  }

  const rssR = olsRSS(XR, yR);
  const rssU = olsRSS(XU, yU);
  if (rssR === null || rssU === null) {
    return { fStat: null, pValue: null, causalityDetected: false, sufficientData: true };
  }

  const n = yR.length;
  const fStat = ((rssR - rssU) / pLag) / (rssU / (n - 2 * pLag - 1));
  const pValue = fDistPValue(fStat, pLag, n - 2 * pLag - 1);
  return { fStat, pValue, causalityDetected: pValue < 0.05, sufficientData: true };
}

/**
 * EWMA correlation (DCC-GARCH proxy): exponentially weighted moving correlation
 * with RiskMetrics decay λ=0.94. Indicates recent dynamic correlation.
 */
export function computeEWMACorrelation(
  macroPoints: MacroPoint[],
  monthlyPrices: MonthlyPrice[],
  decayFactor = 0.94,
): { currentCorr: number | null; trend: 'strengthening' | 'weakening' | 'stable' | null; sufficientData: boolean } {
  const macroMap = new Map(macroPoints.map((p) => [p.date.slice(0, 7), p.value]));
  const priceMap = new Map(monthlyPrices.map((p) => [p.yearMonth, p.close]));
  const common = [...macroMap.keys()].filter((k) => priceMap.has(k)).sort();

  if (common.length < 24) return { currentCorr: null, trend: null, sufficientData: false };

  const macroVals = common.map((k) => macroMap.get(k)!);
  const priceVals = common.map((k) => priceMap.get(k)!);
  const mx = macroVals.slice(1).map((v, i) => v - macroVals[i]);
  const my = priceVals.slice(1).map((v, i) => v - priceVals[i]);
  const n = mx.length;
  const mxMean = mx.reduce((a, b) => a + b, 0) / n;
  const myMean = my.reduce((a, b) => a + b, 0) / n;
  const x = mx.map((v) => v - mxMean);
  const y = my.map((v) => v - myMean);

  // Initialise from first 12 observations
  let varX = x.slice(0, 12).reduce((s, v) => s + v ** 2, 0) / 12;
  let varY = y.slice(0, 12).reduce((s, v) => s + v ** 2, 0) / 12;
  let covXY = x.slice(0, 12).reduce((s, v, i) => s + v * y[i], 0) / 12;

  const λ = decayFactor;
  const corrHistory: number[] = [];

  for (let i = 12; i < n; i++) {
    varX = λ * varX + (1 - λ) * x[i] ** 2;
    varY = λ * varY + (1 - λ) * y[i] ** 2;
    covXY = λ * covXY + (1 - λ) * x[i] * y[i];
    const den = Math.sqrt(varX * varY);
    corrHistory.push(den > 0 ? Math.max(-1, Math.min(1, covXY / den)) : 0);
  }

  if (corrHistory.length < 6) return { currentCorr: null, trend: null, sufficientData: false };

  const currentCorr = corrHistory[corrHistory.length - 1];
  const recent = corrHistory.slice(-6).reduce((a, b) => a + b, 0) / 6;
  const prev = corrHistory.length >= 12
    ? corrHistory.slice(-12, -6).reduce((a, b) => a + b, 0) / 6
    : recent;
  const delta = recent - prev;
  const trend = Math.abs(delta) < 0.05 ? 'stable' : delta > 0 ? 'strengthening' : 'weakening';

  return { currentCorr, trend, sufficientData: true };
}

/**
 * Regime correlation (HMM proxy): splits the macro series at its median into
 * high / low regimes, computes Pearson correlation for each, and identifies
 * the current regime. Requires 36+ aligned months (3+ years).
 */
export function computeRegimeCorrelation(
  macroPoints: MacroPoint[],
  monthlyPrices: MonthlyPrice[],
): {
  highRegime: { pearsonR: number | null; label: string };
  lowRegime: { pearsonR: number | null; label: string };
  currentRegime: 'high' | 'low' | null;
  sufficientData: boolean;
} {
  const na = { highRegime: { pearsonR: null, label: '—' }, lowRegime: { pearsonR: null, label: '—' }, currentRegime: null as null, sufficientData: false };
  const macroMap = new Map(macroPoints.map((p) => [p.date.slice(0, 7), p.value]));
  const priceMap = new Map(monthlyPrices.map((p) => [p.yearMonth, p.close]));
  const common = [...macroMap.keys()].filter((k) => priceMap.has(k)).sort();

  if (common.length < 36) return na;

  const macroVals = common.map((k) => macroMap.get(k)!);
  const priceVals = common.map((k) => priceMap.get(k)!);
  const sorted = [...macroVals].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];

  const highX: number[] = [], highY: number[] = [];
  const lowX: number[] = [], lowY: number[] = [];
  for (let i = 0; i < macroVals.length; i++) {
    if (macroVals[i] >= median) { highX.push(macroVals[i]); highY.push(priceVals[i]); }
    else { lowX.push(macroVals[i]); lowY.push(priceVals[i]); }
  }

  function regimeLabel(r: number | null): string {
    if (r === null) return '—';
    const abs = Math.abs(r), sign = r >= 0 ? '+' : '';
    if (abs >= 0.5) return `${sign}${r.toFixed(2)} (strong)`;
    if (abs >= 0.3) return `${sign}${r.toFixed(2)} (moderate)`;
    return `${sign}${r.toFixed(2)} (weak)`;
  }

  const highR = pearson(highX, highY);
  const lowR = pearson(lowX, lowY);
  return {
    highRegime: { pearsonR: highR, label: regimeLabel(highR) },
    lowRegime: { pearsonR: lowR, label: regimeLabel(lowR) },
    currentRegime: macroVals[macroVals.length - 1] >= median ? 'high' : 'low',
    sufficientData: true,
  };
}
